import { useCallback, useEffect, useState } from 'react';
import { X, Copy, Check, Send, Loader2, Link2, MessageCircle, Clock, Eye, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { backdropClose } from '../../utils/backdropClose';
import {
  fetchLinkCapabilities, generatePaymentLink, sendPaymentLink, linksForPurchase,
  type LinkCapabilities, type PaymentLink,
} from './paymentLinkApi';

/* The one place the desk hands a member a payment link: from the POS after a
   "Send payment link" sale, from a sale's "Collect online" panel, and from a
   due / partial / overdue row on the Purchases page.

   Opening it generates the link — or returns the open one this sale already
   has (same sale, same amount), so opening it again is how staff resend. A
   link whose expiry has passed is never returned; a fresh one is minted and
   the old one stays dead. Sending on WhatsApp goes through the gym's
   `payment_link` automatic template and is offered only when the super admin
   has enabled it; copying always works. */

const money = (n: number) => `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' });

function expiryLine(expiresAt: string) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  const hours = Math.floor(ms / 3600000);
  const soon = hours < 24;
  const left = soon ? (hours < 1 ? 'less than an hour' : `${hours} h`) : `${Math.round(ms / 86400000)} days`;
  return { text: `Valid until ${when(expiresAt)} · ${left} left`, soon };
}

export default function PaymentLinkModal({ gymId, memberId, purchaseId, memberName, memberPhone, onClose }: {
  gymId: string;
  memberId: string;
  purchaseId: string;
  memberName?: string | null;
  memberPhone?: string | null;
  /** Called when the modal closes; the link may have been created or sent. */
  onClose: () => void;
}) {
  const [caps, setCaps] = useState<LinkCapabilities | null>(null);
  const [link, setLink] = useState<PaymentLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);

  const open = useCallback(async () => {
    setError(null);
    try {
      const c = await fetchLinkCapabilities(gymId);
      setCaps(c);
      if (!c.onlinePayments) return;
      setLink(await generatePaymentLink(gymId, memberId, [purchaseId]));
    } catch (e: any) {
      setError(e.message || 'Could not create the payment link');
    }
  }, [gymId, memberId, purchaseId]);

  useEffect(() => { open(); }, [open]);

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — select the link and copy it by hand');
    }
  };

  const send = async () => {
    if (!link) return;
    setSending(true);
    try {
      await sendPaymentLink(gymId, link.id);
      toast.success(link.sendCount > 0 ? 'Sent again on WhatsApp' : 'Payment link sent on WhatsApp');
      const fresh = (await linksForPurchase(gymId, purchaseId)).find(l => l.id === link.id);
      setLink(fresh ?? { ...link, status: 'sent', sendCount: link.sendCount + 1, lastSentAt: new Date().toISOString() });
    } catch (e: any) {
      toast.error(`${e.message}. You can copy the link instead.`);
    } finally {
      setSending(false);
    }
  };

  const expiry = link ? expiryLine(link.expiresAt) : null;
  const sentBefore = !!link && link.sendCount > 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1100] p-4" {...backdropClose(onClose)}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
          <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
            <Link2 className="w-4 h-4 text-indigo-700" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900">Payment link</p>
            {memberName && <p className="text-xs text-slate-500 truncate">{memberName}{memberPhone ? ` · ${memberPhone}` : ''}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          {error ? (
            <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p>{error}</p>
                <button onClick={open} className="mt-1 text-xs font-bold underline">Try again</button>
              </div>
            </div>
          ) : caps && !caps.onlinePayments ? (
            <p className="text-sm text-slate-600">
              Online payments aren’t set up for your gym yet, so a payment link can’t be created. Contact Gympe support to turn them on.
            </p>
          ) : !link ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Creating link…
            </div>
          ) : (
            <>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Amount to pay</p>
                <p className="text-2xl font-black text-slate-900 tabular-nums">{money(link.amount)}</p>
              </div>

              <div className="flex items-stretch gap-2">
                <input
                  readOnly
                  value={link.url}
                  onFocus={e => e.currentTarget.select()}
                  className="flex-1 min-w-0 px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs font-mono text-slate-700 outline-none focus:border-indigo-300"
                />
                <button
                  onClick={copy}
                  className={`px-3 rounded-xl border text-xs font-black uppercase tracking-widest flex items-center gap-1.5 shrink-0 transition-colors ${copied ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
                </button>
              </div>

              <div className="space-y-1 text-xs">
                <p className={`flex items-center gap-1.5 ${expiry!.soon ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>
                  <Clock className="w-3.5 h-3.5 shrink-0" /> {expiry!.text}
                </p>
                <p className="flex items-center gap-1.5 text-slate-500">
                  <MessageCircle className="w-3.5 h-3.5 shrink-0" />
                  {sentBefore
                    ? `Sent on WhatsApp${link.sendCount > 1 ? ` ${link.sendCount}×` : ''} · last ${when(link.lastSentAt!)}`
                    : 'Not sent yet'}
                </p>
                {link.openedAt && (
                  <p className="flex items-center gap-1.5 text-slate-500">
                    <Eye className="w-3.5 h-3.5 shrink-0" /> Opened by the member {when(link.openedAt)}
                  </p>
                )}
              </div>

              {caps?.canSendLinkOnWhatsApp ? (
                <button
                  onClick={send}
                  disabled={sending}
                  className="w-full py-3 rounded-xl bg-indigo-700 text-white text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-indigo-800 disabled:opacity-50"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sentBefore ? 'Send again on WhatsApp' : 'Send on WhatsApp'}
                </button>
              ) : (
                <p className="text-[11px] text-slate-500 bg-slate-50 rounded-xl p-3">
                  To send links on WhatsApp, the “Payment link” message needs to be enabled for your gym — contact Gympe support. Meanwhile, copy the link and share it with the member.
                </p>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-800">Done</button>
        </div>
      </div>
    </div>
  );
}
